import classNames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useFormContext } from 'react-hook-form';
import { IoMdClose } from 'react-icons/io';

import images from '../../../assets/images';
import Input from '../../../components/form/Input';
import SearchInputDropdown from '../../../components/form/SearchInputDropdown';
import UploadFileBox from '../../../components/UploadFileBox';
import { PintoCategory, PintoProductCampaignStatus, PintoProductCampaignUserType, UserType } from '../../../core/graphql/types';
import useQueryPintoCategories from '../../../hooks/useQueryPintoCategories';
import useSearchTags from '../../../hooks/useQuerySearchTags';
import useQuerySearchUsers from '../../../hooks/useQuerySearchUsers';
import { addDate, endOfDate, isSameDate, startOfDate, subtractDate } from '../../../utils/dayjs';
import DropdownUserRowItem from '../../ebooks/components/DropdownUserRowItem';
import { IPropsSelectedUser } from '../../ebooks/components/UserProfile';
import UserTypeSelector from '../components/UserTypeSelector';
import { WRITER_REGISTER_CAMPAIGN_FIELD_KEYS, IWriterRegisterCampaignFields } from '../validates/writerRegisterCampaignSchema';

export type WriterRegisterCampaignStatus = PintoProductCampaignStatus;

export const CAMPAIGN_STATUS = {
  [PintoProductCampaignStatus.Pending]: 'รอดำเนินการ',
  [PintoProductCampaignStatus.RegistrationStarted]: 'กำลังเปิดรับสมัคร',
  [PintoProductCampaignStatus.RegistrationEnded]: 'ปิดรับสมัคร',
  [PintoProductCampaignStatus.Started]: 'กำลังดำเนินแคมเปญ',
  [PintoProductCampaignStatus.Ended]: 'สิ้นสุดแคมเปญ',
};

export const CAMPAIGN_STATUS_CLASS_NAME = {
  [PintoProductCampaignStatus.Pending]: 'badge badge-outline',
  [PintoProductCampaignStatus.RegistrationStarted]: 'badge badge-warning badge-outline',
  [PintoProductCampaignStatus.RegistrationEnded]: 'badge badge-warning badge-outline',
  [PintoProductCampaignStatus.Started]: 'badge badge-success badge-outline',
  [PintoProductCampaignStatus.Ended]: 'badge badge-error badge-outline',
};

type IFlatCategory = {
  id: number;
  label: string;
};

type ICategoryNode = PintoCategory | null | undefined;

const flattenCategories = (categories: (PintoCategory | null | undefined)[] = [], parentNames: string[] = []): IFlatCategory[] => {
  return categories.flatMap((category) => {
    if (!category?._id) return [];
    const id = Number(category._id);
    if (Number.isNaN(id)) return [];

    const names = [...parentNames, category.name || 'Unknown'];
    const current: IFlatCategory = { id, label: names.join(' > ') };
    const children = flattenCategories((category.childrens || []) as (PintoCategory | null | undefined)[], names);

    return [current, ...children];
  });
};

const buildCategoryDescendantsMap = (categories: ICategoryNode[] = []): Map<number, number[]> => {
  const descendantsMap = new Map<number, number[]>();

  const walk = (nodes: ICategoryNode[] = []): number[] => {
    const collectedIds: number[] = [];

    nodes.forEach((node) => {
      if (!node?._id) return;

      const nodeId = Number(node._id);
      if (Number.isNaN(nodeId)) return;

      const childNodes = (node.childrens || []) as ICategoryNode[];
      const childIds = walk(childNodes);

      descendantsMap.set(nodeId, childIds);
      collectedIds.push(nodeId, ...childIds);
    });

    return Array.from(new Set(collectedIds));
  };

  walk(categories);

  return descendantsMap;
};

interface IPropsWriterRegisterCampaignDetailPresenter {
  status?: WriterRegisterCampaignStatus;
  readOnly?: boolean;
  categoryReadOnly?: boolean;
  promoteTemplateImageFile?: File;
  promoteTemplateImageUrl?: string;
  templateOgImageFile?: File;
  templateOgImageUrl?: string;
  bannerImageFile?: File;
  bannerImageUrl?: string;
  onSelectPromoteTemplateImage: (file: File | undefined) => void;
  onSelectTemplateOgImage: (file: File | undefined) => void;
  onSelectBannerImage: (file: File | undefined) => void;
}

const WriterRegisterCampaignDetailPresenter = (props: IPropsWriterRegisterCampaignDetailPresenter) => {
  const {
    status,
    readOnly,
    categoryReadOnly,
    promoteTemplateImageFile,
    promoteTemplateImageUrl,
    templateOgImageFile,
    templateOgImageUrl,
    bannerImageFile,
    bannerImageUrl,
    onSelectPromoteTemplateImage,
    onSelectTemplateOgImage,
    onSelectBannerImage,
  } = props;

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const { pintoCategories } = useQueryPintoCategories();
  const { searchUsers, fetchSearchUsers, isLoadingSearchUsers } = useQuerySearchUsers();
  const { fetchSearchTags, searchTags } = useSearchTags();

  const [
    registerStartDate,
    registerEndDate,
    periodStartDate,
    periodEndDate,
    userType,
    conditions = [],
    tags = [],
    category = 'all',
    categoryIds = [],
    exceptCategoryIds = [],
    specificWriterUserIds = [],
  ] = watch([
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_START_DATE,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_END_DATE,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_START_DATE,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_END_DATE,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.USER_TYPE,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS,
    WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.SPECIFIC_WRITER_USER_IDS,
  ]);

  const [inputSearchText, setInputSearchText] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [tagNameSearch, setTagNameSearch] = useState('');

  useEffect(() => {
    const currentTags = (tags || []) as string[];
    const normalizedTags = currentTags.map((tag) => tag?.trim()).filter(Boolean) as string[];

    if (JSON.stringify(currentTags) !== JSON.stringify(normalizedTags)) {
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, normalizedTags, { shouldValidate: false });
    }
  }, [setValue, tags]);

  useEffect(() => {
    if (userType === PintoProductCampaignUserType.Specific) {
      fetchSearchUsers({ q: query, filter: { type: UserType.Individual } });
    }
  }, [fetchSearchUsers, query, userType]);

  const mappedUserItems = useMemo(() => {
    return searchUsers?.edges.map(({ node }) => ({
      label: node?.displayName || '',
      value: node?._id || '',
      node: <DropdownUserRowItem profileImage={node?.profileImage || ''} displayName={node?.displayName || ''} userName={node?.username || ''} isPremium={node.isPremium} />,
    }));
  }, [searchUsers?.edges]);

  const selectedSpecificUsers = useMemo<IPropsSelectedUser[]>(() => {
    const userMap = new Map((searchUsers?.edges || []).map(({ node }) => [node?._id, node]));
    return (specificWriterUserIds || []).map((id: string) => userMap.get(id)).filter(Boolean) as IPropsSelectedUser[];
  }, [searchUsers?.edges, specificWriterUserIds]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeQueryString = useCallback(
    debounce((value) => setQuery(value), 500),
    [],
  );

  const onChangeSearchText = useCallback(
    (text?: string) => {
      setInputSearchText(text);
      onChangeQueryString(text);
    },
    [onChangeQueryString],
  );

  const onSearchTagName = useCallback(
    (keyword: string) => {
      if (keyword) {
        fetchSearchTags(keyword, { isReserved: false });
      }
    },
    [fetchSearchTags],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeSearchTagName = useCallback(
    debounce((searchText: string) => {
      onSearchTagName(searchText);
    }, 300),
    [],
  );

  const onChangeTagSearchText = useCallback(
    (text: string) => {
      setTagNameSearch(text);
      onChangeSearchTagName(text);
    },
    [onChangeSearchTagName],
  );

  const categoryOptions = useMemo(() => {
    return flattenCategories((pintoCategories?.pintoCategories || []) as (PintoCategory | null | undefined)[]);
  }, [pintoCategories?.pintoCategories]);

  const categoryDescendantsMap = useMemo(() => {
    return buildCategoryDescendantsMap((pintoCategories?.pintoCategories || []) as ICategoryNode[]);
  }, [pintoCategories?.pintoCategories]);

  const allCategoryIds = useMemo(() => categoryOptions.map((item) => item.id), [categoryOptions]);
  const isAllIncluded = useMemo(() => {
    return allCategoryIds.length > 0 && allCategoryIds.every((id) => categoryIds.includes(id));
  }, [allCategoryIds, categoryIds]);

  useEffect(() => {
    if (category !== 'all') return;
    if (!allCategoryIds.length) return;
    if (isAllIncluded) return;

    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS as IWriterRegisterCampaignFields, allCategoryIds, { shouldValidate: true });
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS as IWriterRegisterCampaignFields, [], { shouldValidate: true });
  }, [allCategoryIds, category, isAllIncluded, setValue]);

  const updateConditions = useCallback(
    (index: number, value: string) => {
      const nextConditions = [...(conditions || [])];
      nextConditions[index] = value;
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS as IWriterRegisterCampaignFields, nextConditions, { shouldValidate: true });
    },
    [conditions, setValue],
  );

  const addCondition = useCallback(() => {
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS as IWriterRegisterCampaignFields, [...(conditions || []), ''], { shouldValidate: false });
  }, [conditions, setValue]);

  const removeCondition = useCallback(
    (index: number) => {
      const nextConditions = [...(conditions || [])];
      nextConditions.splice(index, 1);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CONDITIONS as IWriterRegisterCampaignFields, nextConditions, { shouldValidate: true });
    },
    [conditions, setValue],
  );

  const onSelectedTagItem = useCallback(
    (item: { label: string; value: string }) => {
      const selectedTag = item?.label?.trim();
      if (!selectedTag) return;
      if ((tags || []).find((tag: string) => tag?.trim() === selectedTag)) return;

      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, [...(tags || []), selectedTag], { shouldValidate: false });
      setTagNameSearch('');
    },
    [setValue, tags],
  );

  const onEnterNewTagName = useCallback(() => {
    const newTag = tagNameSearch?.trim();
    if (!newTag) return;
    if ((tags || []).find((tag: string) => tag?.trim() === newTag)) return;

    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, [...(tags || []), newTag], { shouldValidate: false });
    onChangeTagSearchText('');
  }, [onChangeTagSearchText, setValue, tagNameSearch, tags]);

  const onRemoveTag = useCallback(
    (index: number) => {
      const nextTags = [...(tags || [])];
      nextTags.splice(index, 1);
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.HASHTAG as IWriterRegisterCampaignFields, nextTags, { shouldValidate: false });
    },
    [setValue, tags],
  );

  const onToggleCategory = useCallback(
    (field: WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS | WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS, categoryId: number) => {
      const source = field === WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS ? [...(categoryIds || [])] : [...(exceptCategoryIds || [])];
      const idsToToggle = field === WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS ? [categoryId, ...(categoryDescendantsMap.get(categoryId) || [])] : [categoryId];

      const shouldUnselect = idsToToggle.every((id) => source.includes(id));
      const nextValue = shouldUnselect ? source.filter((id: number) => !idsToToggle.includes(id)) : Array.from(new Set([...source, ...idsToToggle]));

      setValue(field as IWriterRegisterCampaignFields, nextValue, { shouldValidate: true });

      if (field === WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS && !shouldUnselect) {
        setValue(
          WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS as IWriterRegisterCampaignFields,
          (exceptCategoryIds || []).filter((id: number) => !idsToToggle.includes(id)),
          { shouldValidate: true },
        );
      }

      if (field === WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS) {
        setValue(
          WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY as IWriterRegisterCampaignFields,
          nextValue.length === allCategoryIds.length && allCategoryIds.length > 0 ? 'all' : 'specific',
          { shouldValidate: true },
        );
      }
    },
    [allCategoryIds.length, categoryDescendantsMap, categoryIds, exceptCategoryIds, setValue],
  );

  const onToggleAllIncluded = useCallback(() => {
    const shouldSelectAll = !(category === 'all' || isAllIncluded);
    const nextCategoryIds = shouldSelectAll ? allCategoryIds : [];

    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS as IWriterRegisterCampaignFields, nextCategoryIds, { shouldValidate: true });
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.EXCEPT_CATEGORY_IDS as IWriterRegisterCampaignFields, [], { shouldValidate: true });
    setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY as IWriterRegisterCampaignFields, shouldSelectAll ? 'all' : 'specific', { shouldValidate: true });
  }, [allCategoryIds, category, isAllIncluded, setValue]);

  const onSelectSpecificUser = useCallback(
    (userId: string) => {
      if (!userId) return;
      if ((specificWriterUserIds || []).includes(userId)) return;
      setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.SPECIFIC_WRITER_USER_IDS as IWriterRegisterCampaignFields, [...(specificWriterUserIds || []), userId], {
        shouldValidate: true,
      });
    },
    [setValue, specificWriterUserIds],
  );

  const onRemoveSpecificUser = useCallback(
    (userId?: string) => {
      if (!userId) return;
      setValue(
        WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.SPECIFIC_WRITER_USER_IDS as IWriterRegisterCampaignFields,
        (specificWriterUserIds || []).filter((id: string) => id !== userId),
        {
          shouldValidate: true,
        },
      );
    },
    [setValue, specificWriterUserIds],
  );

  const currentDate = useMemo(() => new Date(), []);
  const isCategorySectionReadOnly = !!categoryReadOnly;
  const minRegisterStartDateTime = useMemo(() => addDate(currentDate, 1, 'day').startOf('day').toDate(), [currentDate]);

  // Register start date handlers
  const getRegisterStartDateMinTime = useCallback(() => {
    if (!registerStartDate || (registerStartDate && isSameDate(registerStartDate, minRegisterStartDateTime))) {
      return minRegisterStartDateTime;
    }
    return startOfDate(registerStartDate).toDate();
  }, [minRegisterStartDateTime, registerStartDate]);

  const getRegisterStartDateMaxTime = useCallback(() => {
    if (registerEndDate && isSameDate(registerEndDate, registerStartDate)) {
      return subtractDate(registerEndDate, 1, 'minute').toDate();
    }
    return endOfDate(registerStartDate || currentDate).toDate();
  }, [currentDate, registerEndDate, registerStartDate]);

  // Register end date handlers
  const getRegisterEndDateMinTime = useCallback(() => {
    if ((registerStartDate && !registerEndDate) || (registerEndDate && isSameDate(registerEndDate, registerStartDate))) {
      return addDate(registerStartDate, 1, 'minute').toDate();
    }
    if (!registerEndDate || (registerEndDate && isSameDate(registerEndDate, minRegisterStartDateTime))) {
      return minRegisterStartDateTime;
    }
    return startOfDate(registerEndDate || currentDate).toDate();
  }, [currentDate, minRegisterStartDateTime, registerEndDate, registerStartDate]);

  const getRegisterEndDateMaxTime = useCallback(() => {
    return endOfDate(registerStartDate || registerEndDate || currentDate).toDate();
  }, [currentDate, registerEndDate, registerStartDate]);

  // Period start date handlers
  const getPeriodStartDateMinTime = useCallback(() => {
    if (!periodStartDate || (periodStartDate && isSameDate(periodStartDate, currentDate))) {
      return addDate(currentDate, 1, 'minute').toDate();
    }
    return startOfDate(periodStartDate).toDate();
  }, [currentDate, periodStartDate]);

  const getPeriodStartDateMaxTime = useCallback(() => {
    if (periodEndDate && isSameDate(periodEndDate, periodStartDate)) {
      return subtractDate(periodEndDate, 1, 'minute').toDate();
    }
    return endOfDate(periodStartDate || currentDate).toDate();
  }, [currentDate, periodEndDate, periodStartDate]);

  // Period end date handlers
  const getPeriodEndDateMinTime = useCallback(() => {
    if ((periodStartDate && !periodEndDate) || (periodEndDate && isSameDate(periodEndDate, periodStartDate))) {
      return addDate(periodStartDate, 1, 'minute').toDate();
    }
    if (!periodEndDate || (periodEndDate && isSameDate(periodEndDate, currentDate))) {
      return addDate(currentDate, 1, 'minute').toDate();
    }
    return startOfDate(periodEndDate || currentDate).toDate();
  }, [currentDate, periodEndDate, periodStartDate]);

  const getPeriodEndDateMaxTime = useCallback(() => {
    return endOfDate(periodStartDate || periodEndDate || currentDate).toDate();
  }, [currentDate, periodEndDate, periodStartDate]);

  return (
    <div className="h-full space-y-12">
      {status && (
        <div className="flex items-center justify-end">
          สถานะ: <span className={classNames('ml-4', CAMPAIGN_STATUS_CLASS_NAME[status])}>{CAMPAIGN_STATUS[status]}</span>
        </div>
      )}

      {/* Campaign Title */}
      <Input
        name={WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.TITLE}
        register={register}
        label="ชื่อแคมเปญ"
        required
        placeholder="กรอกชื่อแคมเปญ"
        helperText={errors.title?.message as string}
        isInvalid={!!errors.title}
        disabled={!!readOnly}
      />

      {/* Conditions */}
      <div className="form-control">
        <label className="label">
          <span className="label-text text-systemGrays02LabelSecondary">
            เงื่อนไข <span className="text-colorsBrandRed01Primary">*</span>
          </span>
        </label>
        <div className="space-y-8">
          {(conditions || []).map((item: string, index: number) => (
            <div key={`condition-${index}`} className="flex w-full items-center gap-8">
              <input
                value={item}
                onChange={(e) => updateConditions(index, e.target.value)}
                placeholder={`เงื่อนไข ${index + 1}`}
                disabled={!!readOnly}
                className={classNames('input input-bordered w-full font-tt', {
                  'input-error': !!errors.conditions,
                })}
              />
              {!readOnly && (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeCondition(index)}>
                  ลบ
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button type="button" className="btn btn-outline btn-sm" onClick={addCondition}>
              + เพิ่มเงื่อนไข
            </button>
          )}
        </div>
        {errors.conditions && (
          <label className="pt-4 label">
            <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.conditions?.message as string}</span>
          </label>
        )}
      </div>

      {/* Type (User Type) & Category */}
      <div className="flex flex-col space-y-16 p-16 border border-solid rounded-lg border-colorsBrandWarmBlack04LabelQuaternary">
        <UserTypeSelector disabled={!!readOnly} />

        {userType === PintoProductCampaignUserType.Specific && (
          <div className="space-y-8">
            <label className="label">
              <span className="label-text text-systemGrays02LabelSecondary">
                ผู้เขียนเฉพาะเจาะจง <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>

            <div className="form-control">
              <label className="label">
                <span className="label-text">ค้นหาผู้ใช้*</span>
              </label>
              <SearchInputDropdown
                items={mappedUserItems}
                value={inputSearchText}
                onChange={(value) => onChangeSearchText(value)}
                menuClassName="w-full"
                isLoading={isLoadingSearchUsers}
                disabled={!!readOnly}
                onClickMenuItem={(selectItem) => {
                  onSelectSpecificUser(selectItem.value);
                }}
                placeholder="User ID, ชื่อผู้ใช้ หรืออีเมล"
              />
            </div>

            {!!selectedSpecificUsers.length && (
              <div className="grid grid-cols-2 gap-8">
                {selectedSpecificUsers.map((item: IPropsSelectedUser, index: number) => (
                  <div key={`${item?._id || ''}-${index}`} className="border border-base-content/10 rounded-lg px-12 py-8 flex items-center justify-between">
                    <DropdownUserRowItem
                      profileImage={item?.profileImage || ''}
                      displayName={item?.displayName || ''}
                      userName={item?.username || ''}
                      isPremium={item?.isPremium || false}
                    />
                    {!readOnly && (
                      <button type="button" className="btn btn-ghost btn-xs" onClick={() => onRemoveSpecificUser(item?._id || undefined)}>
                        ลบ
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!!errors.specificWriterUserIds && (
              <label className="pt-4 label">
                <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.specificWriterUserIds?.message as string}</span>
              </label>
            )}
          </div>
        )}

        <div className="space-y-8">
          <label className="label">
            <span className="label-text text-systemGrays02LabelSecondary">
              หมวดหมู่ <span className="text-colorsBrandRed01Primary">*</span>
            </span>
          </label>

          <div>
            <div className="border border-solid rounded-lg p-12 max-h-[220px] overflow-y-auto">
              <div className="font-semibold mb-8">หมวดหมู่ที่เข้าร่วม</div>
              <div className="space-y-4">
                <label className="flex items-center gap-8" key="include-all">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={category === 'all' || isAllIncluded}
                    disabled={isCategorySectionReadOnly}
                    onChange={onToggleAllIncluded}
                  />
                  <span className="text-sm font-semibold">ทั้งหมด</span>
                </label>
                {categoryOptions.map((cat) => (
                  <label className="flex items-center gap-8" key={`include-${cat.id}`}>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={category === 'all' ? !(exceptCategoryIds || []).includes(cat.id) : (categoryIds || []).includes(cat.id)}
                      disabled={isCategorySectionReadOnly || (exceptCategoryIds || []).includes(cat.id)}
                      onChange={() => onToggleCategory(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.CATEGORY_IDS, cat.id)}
                    />
                    <span className={classNames('text-sm', { 'text-systemGrays03LabelTertiary': (exceptCategoryIds || []).includes(cat.id) })}>{cat.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {!!errors.categoryIds && (
            <label className="pt-4 label">
              <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.categoryIds?.message as string}</span>
            </label>
          )}
        </div>
      </div>

      {/* Register Period */}
      <div className="p-16 border border-solid rounded-lg border-colorsBrandWarmBlack04LabelQuaternary">
        <h3 className="mb-12 text-lg font-semibold">ช่วงเวลารับสมัคร</h3>
        <div className="flex gap-x-16">
          <div className="flex flex-col flex-1">
            <label className="label label-text text-systemGrays02LabelSecondary">
              <span className="label-text text-systemGrays02LabelSecondary">
                วันเวลาเริ่มรับสมัคร <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>
            <DatePicker
              className={classNames('input input-bordered w-full font-tt text-systemGrays02LabelSecondary font-semibold', {
                '!input-error': errors.registerStartDate,
              })}
              required
              timeFormat="HH:mm"
              timeIntervals={5}
              timeCaption="เวลา"
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              selected={registerStartDate ? new Date(registerStartDate) : undefined}
              minDate={minRegisterStartDateTime}
              minTime={getRegisterStartDateMinTime()}
              maxTime={getRegisterStartDateMaxTime()}
              onChange={(date: Date | null) => {
                setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_START_DATE as IWriterRegisterCampaignFields, date?.toISOString());
              }}
              disabled={!!readOnly}
            />
            {errors.registerStartDate && (
              <label className="pt-4 label">
                <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.registerStartDate?.message as string}</span>
              </label>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <label className="label label-text text-systemGrays02LabelSecondary">
              <span className="label-text text-systemGrays02LabelSecondary">
                วันเวลาสิ้นสุดรับสมัคร <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>
            <DatePicker
              className={classNames('input input-bordered w-full font-tt text-systemGrays02LabelSecondary font-semibold', {
                '!input-error': errors.registerEndDate,
              })}
              required
              timeFormat="HH:mm"
              timeIntervals={5}
              timeCaption="เวลา"
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              selected={registerEndDate ? new Date(registerEndDate) : undefined}
              minDate={registerStartDate ? new Date(registerStartDate) : minRegisterStartDateTime}
              minTime={getRegisterEndDateMinTime()}
              maxTime={getRegisterEndDateMaxTime()}
              onChange={(date: Date | null) => {
                setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.REGISTER_END_DATE as IWriterRegisterCampaignFields, date?.toISOString());
              }}
              disabled={!!readOnly}
            />
            {errors.registerEndDate && (
              <label className="pt-4 label">
                <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.registerEndDate?.message as string}</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Campaign Period */}
      <div className="p-16 border border-solid rounded-lg border-colorsBrandWarmBlack04LabelQuaternary">
        <h3 className="mb-12 text-lg font-semibold">ช่วงเวลาแคมเปญ</h3>
        <div className="flex gap-x-16">
          <div className="flex flex-col flex-1">
            <label className="label label-text text-systemGrays02LabelSecondary">
              <span className="label-text text-systemGrays02LabelSecondary">
                วันเวลาเริ่มแคมเปญ <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>
            <DatePicker
              className={classNames('input input-bordered w-full font-tt text-systemGrays02LabelSecondary font-semibold', {
                '!input-error': errors.periodStartDate,
              })}
              required
              timeFormat="HH:mm"
              timeIntervals={5}
              timeCaption="เวลา"
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              selected={periodStartDate ? new Date(periodStartDate) : undefined}
              minDate={registerEndDate ? new Date(registerEndDate) : currentDate}
              minTime={getPeriodStartDateMinTime()}
              maxTime={getPeriodStartDateMaxTime()}
              onChange={(date: Date | null) => {
                setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_START_DATE as IWriterRegisterCampaignFields, date?.toISOString());
              }}
              disabled={!!readOnly}
            />
            {errors.periodStartDate && (
              <label className="pt-4 label">
                <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.periodStartDate?.message as string}</span>
              </label>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <label className="label label-text text-systemGrays02LabelSecondary">
              <span className="label-text text-systemGrays02LabelSecondary">
                วันเวลาสิ้นสุดแคมเปญ <span className="text-colorsBrandRed01Primary">*</span>
              </span>
            </label>
            <DatePicker
              className={classNames('input input-bordered w-full font-tt text-systemGrays02LabelSecondary font-semibold', {
                '!input-error': errors.periodEndDate,
              })}
              required
              timeFormat="HH:mm"
              timeIntervals={5}
              timeCaption="เวลา"
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              selected={periodEndDate ? new Date(periodEndDate) : undefined}
              minDate={periodStartDate ? new Date(periodStartDate) : currentDate}
              minTime={getPeriodEndDateMinTime()}
              maxTime={getPeriodEndDateMaxTime()}
              onChange={(date: Date | null) => {
                setValue(WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.PERIOD_END_DATE as IWriterRegisterCampaignFields, date?.toISOString());
              }}
              disabled={!!readOnly}
            />
            {errors.periodEndDate && (
              <label className="pt-4 label">
                <span className="label-text-alt font-tt text-colorsBrandRed01Primary">{errors.periodEndDate?.message as string}</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Minimum Discount Percentage */}
      <Input
        name={WRITER_REGISTER_CAMPAIGN_FIELD_KEYS.MINIMUM_DISCOUNT_PERCENTAGE}
        register={register}
        label="ส่วนลดขั้นต่ำ (%)"
        required
        type="number"
        min="1"
        max="100"
        placeholder="กรอกส่วนลดขั้นต่ำ (เช่น 10)"
        helperText={(errors.minimumDiscountPercentage?.message as string) || 'กรอกค่าเป็นตัวเลข 1-100%'}
        isInvalid={!!errors.minimumDiscountPercentage}
        disabled={!!readOnly}
      />

      {/* Tags */}
      <div className="form-control">
        <label className="label">
          <span className="label-text text-systemGrays02LabelSecondary">แท็ก</span>
        </label>

        {(tags?.length || 0) > 0 && (
          <div className="flex flex-wrap gap-8 p-16 border-[1px] border-solid border-colorsBrandWarmBlack04LabelQuaternary rounded-6 min-h-[48px]">
            {tags?.map((item: string, index: number) => (
              <button
                key={`${item}-${index}`}
                type="button"
                onClick={() => onRemoveTag(index)}
                disabled={!!readOnly}
                className="h-[30px] px-8 py-4 border border-systemGrays02LabelSecondary rounded-box flex justify-between items-center gap-x-8 disabled:opacity-60"
              >
                <div>{item}</div>
                {!readOnly && (
                  <div className="p-4 pr-0 bg-componentsBgGrouped02 rounded-box">
                    <IoMdClose color="var(--systemGrays01LabelPrimary)" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <SearchInputDropdown
          items={searchTags?.edges?.map(({ node }) => ({ label: node?.name || '', value: node?._id || '' })) || []}
          value={tagNameSearch}
          onChange={onChangeTagSearchText}
          menuClassName="w-full"
          onClickMenuItem={onSelectedTagItem}
          placeholder="ใส่แท็กที่ต้องการ"
          disabled={!!readOnly}
          onKeydown={(e) => {
            if (e.key === 'Enter') {
              onEnterNewTagName();
            }
          }}
        />
      </div>

      {/* Cover & Banner Image */}
      <div className="space-y-24">
        <div className="flex flex-col justify-between w-full space-x-0 md:flex-row md:space-x-24">
          <div className="flex-1">
            <UploadFileBox
              label="ตัวอย่างรูปโปรโมต"
              acceptFileExtension=".jpg,.jpeg,.png,.webp"
              fileName={promoteTemplateImageFile?.name}
              fileUrl={promoteTemplateImageUrl}
              onSelectFile={onSelectPromoteTemplateImage}
              readOnly={!!readOnly}
              helperText="รองรับไฟล์ PNG, JPG"
            />
            <div className="w-[250px] h-[250px] bg-colorsBrandWarmBlack04LabelQuaternary rounded-6">
              <img
                className="w-[250px] h-[250px] rounded-6 object-cover"
                src={promoteTemplateImageFile ? URL.createObjectURL(promoteTemplateImageFile) : promoteTemplateImageUrl || images.pinto.default.ebookCover}
              />
            </div>
          </div>

          <div className="flex-1">
            <UploadFileBox
              label="รูปโปรโมตแคมเปญ (ใช้สำหรับสร้างภาพที่มีราคาและส่วนลด)"
              acceptFileExtension=".jpg,.jpeg,.png,.webp"
              fileName={templateOgImageFile?.name}
              fileUrl={templateOgImageUrl}
              onSelectFile={onSelectTemplateOgImage}
              readOnly={!!readOnly}
              helperText="รองรับไฟล์ PNG, JPG"
            />
            <div className="w-[250px] h-[250px] bg-colorsBrandWarmBlack04LabelQuaternary rounded-6">
              <img
                className="w-[250px] h-[250px] rounded-6 object-cover"
                src={templateOgImageFile ? URL.createObjectURL(templateOgImageFile) : templateOgImageUrl || images.pinto.default.ebookCover}
              />
            </div>
          </div>
        </div>

        <div className="w-full">
          <UploadFileBox
            label="แบนเนอร์แคมเปญ"
            acceptFileExtension=".jpg,.jpeg,.png,.webp"
            fileName={bannerImageFile?.name}
            fileUrl={bannerImageUrl}
            onSelectFile={onSelectBannerImage}
            readOnly={!!readOnly}
            helperText="รองรับไฟล์ PNG, JPG"
          />
          <div className="w-full md:w-[730px] h-[310px] bg-colorsBrandWarmBlack04LabelQuaternary rounded-6">
            <img
              className="w-full md:w-[730px] h-[310px] rounded-6 object-cover"
              src={bannerImageFile ? URL.createObjectURL(bannerImageFile) : bannerImageUrl || images.pinto.default.bannerCover}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WriterRegisterCampaignDetailPresenter;
